import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import "./Prenotazioni.css";
import { Nullable } from "primereact/ts-helpers";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import { addLocale } from "primereact/api";
import { InputSwitch, InputSwitchChangeEvent } from "primereact/inputswitch";
import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import { Accordion, AccordionTab } from "primereact/accordion";
import { RootState, AppDispatch } from "../Redux/store/index";
import { setAppointment } from "../Redux/reducer/userSlice";
import { Activity, GroupedActivity, Resource, Availability, Options } from "../lib/types";
import { APPOINTMENT_SETTINGS, insurance, location } from "../lib/config";
import BookingService from "../services/api/BookingService";
import AppointmentService from "../services/api/AppointmentService";
import {
  mapResourcesToUI,
  mapAvailabilitiesToUI,
  groupAvailabilitiesByDate,
  formatDateForDisplay,
  formatTimeSlot,
  mapAvailabilityForBooking
} from "../lib/bookingMappers";
import { useErrorHandler } from "../hooks/useServices";
import logger from "../services/Logger";
import { ProgressSpinner } from "primereact/progressspinner";

// Configurazione locale italiana per Calendar
addLocale("it", {
  firstDayOfWeek: 1,
  dayNames: ["Domenica", "Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato"],
  dayNamesShort: ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"],
  dayNamesMin: ["Do", "Lu", "Ma", "Me", "Gi", "Ve", "Sa"],
  monthNames: [
    "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
    "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"
  ],
  monthNamesShort: ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"],
  today: "Oggi",
  clear: "Pulisci"
});

interface PrenotazioniProps {
  activities: Activity[];
  groupedActivities: GroupedActivity[];
  loading: boolean;
}

const Prenotazioni: React.FC<PrenotazioniProps> = ({ activities, groupedActivities, loading }) => {
  // Redux
  const dispatch = useDispatch<AppDispatch>();
  const isLoggedIn = useSelector((state: RootState) => state.user.isLoggedIn);
  const user = useSelector((state: RootState) => state.user);

  // State
  const [medici, setMedici] = useState<Options[]>([]);
  const [selectPrestazione, setSelectPrestazione] = useState<Activity | null>(null);
  const [selectMedico, setSelectMedico] = useState<string | null>(null);
  const [date, setDate] = useState<Nullable<Date>>(null);
  const [startTime, setStartTime] = useState<string>("00:01");
  const [endTime, setEndTime] = useState<string>("23:59");
  const [dialogVisible, setDialogVisible] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Availability | null>(null);
  const [checked, setChecked] = useState<boolean>(false); // Nessuna preferenza medico
  const [medDis, setMedDis] = useState<boolean>(false); // Dropdown medico disabilitato
  const [availabilities, setAvailabilities] = useState<Availability[]>([]);
  const [searching, setSearching] = useState(false);
  const [booking, setBooking] = useState(false);

  const toast = useRef<Toast>(null);
  const { handleError } = useErrorHandler(toast);
  const today = new Date();

  // Genera opzioni orari (intervalli 15 min)
  const generateTimeOptions = (start: string, end: string, interval: number): Options[] => {
    const times: Options[] = [];
    let currentTime = new Date(`1970-01-01T${start}:00`);
    const endTime = new Date(`1970-01-01T${end}:00`);

    while (currentTime <= endTime) {
      const hours = currentTime.getHours().toString().padStart(2, "0");
      const minutes = currentTime.getMinutes().toString().padStart(2, "0");
      times.push({ label: `${hours}:${minutes}`, value: `${hours}:${minutes}` });
      currentTime = new Date(currentTime.getTime() + interval * 60000);
    }

    return times;
  };

  const timeOptions = generateTimeOptions("07:00", "21:00", 15);

  // Calcola data minima prenotazione (oggi + MIN_HOURS_THRESHOLD)
  const getMinDate = (): Date => {
    const minDate = new Date();
    minDate.setHours(minDate.getHours() + APPOINTMENT_SETTINGS.MIN_HOURS_THRESHOLD);
    return minDate;
  };

  // Carica risorse quando seleziona prestazione
  useEffect(() => {
    const loadResources = async () => {
      if (!selectPrestazione) {
        setMedici([]);
        return;
      }

      try {
        logger.debug("Loading resources for activity", { activityId: selectPrestazione.id });

        const backendResources = await BookingService.getResources(
          selectPrestazione.id,
          location,
          insurance
        );

        const resources = mapResourcesToUI(backendResources);

        const resourceOptions: Options[] = resources.map((r) => ({
          label: `${r.name} ${r.surname}`,
          value: r.id
        }));

        setMedici(resourceOptions);
        logger.info("Resources loaded successfully", { count: resourceOptions.length });

      } catch (error) {
        logger.error("Failed to load resources", error);
        handleError(error, "Prenotazioni.loadResources");
      }
    };

    loadResources();
  }, [selectPrestazione]);

  // Gestisci switch "Nessuna preferenza medico"
  const handleSwitchChange = (e: InputSwitchChangeEvent) => {
    setChecked(e.value);
    setMedDis(e.value);
    if (e.value) {
      setSelectMedico(null); // Reset selezione medico
    }
  };

  // Formatta data per API backend
  const formatDate = (date: Date): string => {
    const pad = (n: number) => (n < 10 ? `0${n}` : n);
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  };

  // Cerca disponibilità
  const handleSubmit = async () => {
    // Validazione
    if (!selectPrestazione) {
      toast.current?.show({
        severity: "error",
        summary: "Errore",
        detail: "Seleziona una prestazione."
      });
      return;
    }

    // Calcola date ricerca
    const startDate = date || getMinDate();
    const endDate = new Date(startDate);
    endDate.setMonth(endDate.getMonth() + 3); // Cerca fino a 3 mesi dopo

    try {
      setSearching(true);
      setAvailabilities([]);

      logger.info("Searching availabilities", {
        activityId: selectPrestazione.id,
        resourceId: selectMedico,
        startDate: formatDate(startDate),
        endDate: formatDate(endDate),
        startTime,
        endTime
      });

      const backendAvailabilities = await BookingService.searchAvailabilities({
        activityId: selectPrestazione.id,
        resourceId: selectMedico || undefined,
        startDate,
        endDate,
        minTime: `${startTime}:00`,
        maxTime: `${endTime}:00`,
        locationId: location,
        insuranceId: insurance,
        resultsNumber: 100
      });

      const mappedAvailabilities = mapAvailabilitiesToUI(backendAvailabilities);
      setAvailabilities(mappedAvailabilities);

      if (mappedAvailabilities.length === 0) {
        toast.current?.show({
          severity: "info",
          summary: "Nessuna disponibilità",
          detail: "Non ci sono slot disponibili per i criteri selezionati."
        });
      } else {
        toast.current?.show({
          severity: "success",
          summary: "Ricerca completata",
          detail: `Trovate ${mappedAvailabilities.length} disponibilità.`
        });
      }

    } catch (error) {
      logger.error("Failed to search availabilities", error);
      handleError(error, "Prenotazioni.searchAvailabilities");
    } finally {
      setSearching(false);
    }
  };

  // Apri dialog conferma prenotazione
  const openDialog = (availability: Availability) => {
    setSelectedAppointment(availability);
    setDialogVisible(true);
  };

  // Conferma prenotazione
  const confirmAppointment = async () => {
    if (!selectedAppointment || !selectPrestazione) return;

    setDialogVisible(false);

    // Se non loggato, mostra messaggio e chiudi dialog
    if (!isLoggedIn) {
      logger.info("User not logged in, cannot complete booking");

      toast.current?.show({
        severity: "warn",
        summary: "Login richiesto",
        detail: "Effettua il login per completare la prenotazione.",
        life: 5000
      });

      setDialogVisible(false);
      return;
    }

    // Se loggato, procedi con la prenotazione
    try {
      setBooking(true);

      if (!user.patientInfo) {
        throw new Error("Informazioni paziente mancanti");
      }

      if (!user.patientInfo.patientId) {
        throw new Error("ID paziente mancante");
      }

      logger.info("Creating appointment", {
        availabilityId: selectedAppointment.id,
        patientId: user.patientInfo.patientId
      });

      const appointmentData = mapAvailabilityForBooking(
        selectedAppointment,
        selectPrestazione,
        {
          patientId: user.patientInfo.patientId,
          firstName: user.patientInfo.firstName,
          lastName: user.patientInfo.lastName,
          codiceFiscale: user.codiceFiscale,
          email: user.patientInfo.email || user.email || "",
          phone: user.patientInfo.phone || user.patientInfo.mobilePhone,
          dateOfBirth: user.patientInfo.birthDate || "",
          gender: user.patientInfo.sexId || "M"
        }
      );

      const result = await AppointmentService.addAppointment(appointmentData);

      if (result.Result === "OK") {
        toast.current?.show({
          severity: "success",
          summary: "Prenotazione confermata",
          detail: `Appuntamento creato con successo. ID: ${result.AppId}`,
          life: 5000
        });

        // Reset form
        setAvailabilities([]);
        setSelectPrestazione(null);
        setSelectMedico(null);
        setDate(null);
        setChecked(false);
        setMedDis(false);
      }

    } catch (error) {
      logger.error("Failed to create appointment", error);
      handleError(error, "Prenotazioni.confirmAppointment");
    } finally {
      setBooking(false);
    }
  };

  // Raggruppa availabilities per data
  const groupedAvailabilities = groupAvailabilitiesByDate(availabilities);
  const sortedDates = Object.keys(groupedAvailabilities).sort();

  return (
    <div className="prenotazioni-div">
      <Toast ref={toast} />

      <h2>Prenota un Appuntamento</h2>

      {/* Dropdown Prestazioni */}
      <div style={{ marginTop: "2rem" }}>
        <label htmlFor="prestazione">Seleziona Prestazione</label>
        <Dropdown
          id="prestazione"
          value={selectPrestazione}
          onChange={(e: DropdownChangeEvent) => setSelectPrestazione(e.value)}
          options={groupedActivities}
          optionLabel="name"
          optionGroupLabel="label"
          optionGroupChildren="items"
          placeholder="Seleziona una prestazione"
          filter
          filterBy="name"
          className="dropdown w-full"
          disabled={loading}
          showClear
        />
      </div>

      {/* Dropdown Medici */}
      {selectPrestazione && (
        <div className="seleziona-medico">
          <label htmlFor="medico">Seleziona Medico (opzionale)</label>
          <Dropdown
            id="medico"
            value={selectMedico}
            onChange={(e: DropdownChangeEvent) => setSelectMedico(e.value)}
            options={medici}
            optionLabel="label"
            optionValue="value"
            placeholder="Seleziona un medico"
            filter
            className="dropdown w-full"
            disabled={medDis || medici.length === 0}
            showClear
          />

          {/* Switch Nessuna Preferenza */}
          <div className="switch-prenotazione">
            <label htmlFor="switch-nessuna-preferenza">Nessuna preferenza medico</label>
            <InputSwitch
              id="switch-nessuna-preferenza"
              checked={checked}
              onChange={handleSwitchChange}
            />
          </div>
        </div>
      )}

      {/* Filtri Orari */}
      {selectPrestazione && (
        <div style={{ marginTop: "2rem" }}>
          <div className="grid">
            <div className="col-12 md:col-6">
              <label htmlFor="startTime">Ora Inizio</label>
              <Dropdown
                id="startTime"
                value={startTime}
                onChange={(e) => setStartTime(e.value)}
                options={timeOptions}
                optionLabel="label"
                optionValue="value"
                placeholder="Ora inizio"
                className="w-full"
              />
            </div>
            <div className="col-12 md:col-6">
              <label htmlFor="endTime">Ora Fine</label>
              <Dropdown
                id="endTime"
                value={endTime}
                onChange={(e) => setEndTime(e.value)}
                options={timeOptions}
                optionLabel="label"
                optionValue="value"
                placeholder="Ora fine"
                className="w-full"
              />
            </div>
          </div>
        </div>
      )}

      {/* Filtro Data */}
      {selectPrestazione && (
        <div style={{ marginTop: "2rem" }}>
          <label htmlFor="calendar">Data Inizio Ricerca (opzionale)</label>
          <Calendar
            id="calendar"
            value={date}
            onChange={(e) => setDate(e.value)}
            minDate={getMinDate()}
            dateFormat="dd/mm/yy"
            locale="it"
            showIcon
            placeholder="Seleziona una data"
            className="w-full"
          />
          <small className="block mt-2" style={{ color: "#666" }}>
            Puoi prenotare a partire da {APPOINTMENT_SETTINGS.MIN_HOURS_THRESHOLD} ore da ora.
          </small>
        </div>
      )}

      {/* Button Cerca */}
      {selectPrestazione && (
        <Button
          label={searching ? "Ricerca in corso..." : "CERCA DISPONIBILITÀ"}
          icon={searching ? "pi pi-spin pi-spinner" : "pi pi-search"}
          className="invia-btn w-full"
          onClick={handleSubmit}
          disabled={searching}
        />
      )}

      {/* Loading Spinner */}
      {searching && (
        <div className="flex justify-content-center align-items-center" style={{ marginTop: "2rem" }}>
          <ProgressSpinner />
        </div>
      )}

      {/* Risultati Disponibilità */}
      {!searching && availabilities.length > 0 && (
        <div style={{ marginTop: "3rem" }}>
          <h3>Disponibilità Trovate ({availabilities.length})</h3>
          <Accordion multiple activeIndex={[0]}>
            {sortedDates.map((dateKey, index) => {
              const dateAvailabilities = groupedAvailabilities[dateKey];
              const giorni = ["dom", "lun", "mar", "mer", "gio", "ven", "sab"];
              let _dt: Date;
              if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateKey)) {
                const [_d, _m, _y] = dateKey.split("/");
                _dt = new Date(parseInt(_y, 10), parseInt(_m, 10) - 1, parseInt(_d, 10));
              } else {
                _dt = new Date(dateKey);
              }
              const dayLabel = isNaN(_dt.getTime()) ? "" : giorni[_dt.getDay()];
              const headerNode = (
                <span>
                  <i className="pi pi-calendar" style={{ marginRight: "0.5rem" }}></i>
                  {dayLabel ? `${dayLabel} ` : ""}{formatDateForDisplay(dateKey)} ({dateAvailabilities.length} slot)
                </span>
              );
              return (
                <AccordionTab
                  key={dateKey}
                  header={headerNode}
                >
                  <div className="grid">
                    {dateAvailabilities.map((avail) => (
                      <div key={avail.id} className="col-12 md:col-6 lg:col-4">
                        <div
                          className="availability-card p-3 border-1 border-round cursor-pointer"
                          onClick={() => openDialog(avail)}
                          style={{ borderColor: "#dee2e6" }}
                        >
                          <div className="availability-time">
                            {formatTimeSlot(avail.startTime, avail.endTime)}
                          </div>
                          {avail.price && (
                            <div className="availability-price mt-2">
                              €{avail.price.toFixed(2)}
                            </div>
                          )}
                          <Button
                            label="Prenota"
                            icon="pi pi-check"
                            className="p-button-sm mt-2 w-full"
                            onClick={(e) => {
                              e.stopPropagation();
                              openDialog(avail);
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </AccordionTab>
              );
            })}
          </Accordion>
        </div>
      )}

      {/* Dialog Conferma Prenotazione */}
      <Dialog
        header="Conferma Prenotazione"
        visible={dialogVisible}
        className="responsive-dialog"
        onHide={() => setDialogVisible(false)}
        footer={
          <div>
            <Button
              label="Annulla"
              icon="pi pi-times"
              onClick={() => setDialogVisible(false)}
              className="p-button-text"
              disabled={booking}
            />
            <Button
              label={booking ? "Prenotazione..." : "Conferma"}
              icon={booking ? "pi pi-spin pi-spinner" : "pi pi-check"}
              onClick={confirmAppointment}
              autoFocus
              disabled={booking}
            />
          </div>
        }
      >
        {selectedAppointment && selectPrestazione && (
          <div>
            <p><strong>Prestazione:</strong> {selectPrestazione.name}</p>
            <p><strong>Data:</strong> {formatDateForDisplay(selectedAppointment.date)}</p>
            <p><strong>Orario:</strong> {formatTimeSlot(selectedAppointment.startTime, selectedAppointment.endTime)}</p>
            {selectedAppointment.price && (
              <p><strong>Prezzo:</strong> €{selectedAppointment.price.toFixed(2)}</p>
            )}
            {selectPrestazione.preparation && (
              <div className="mt-3 p-3" style={{ backgroundColor: "#fff3cd", borderRadius: "6px" }}>
                <strong>ℹ️ Preparazione:</strong>
                <p className="mt-2">{selectPrestazione.preparation}</p>
              </div>
            )}
            {!isLoggedIn && (
              <div className="mt-3 p-3" style={{ backgroundColor: "#d1ecf1", borderRadius: "6px" }}>
                <strong>ℹ️ Login Richiesto</strong>
                <p className="mt-2">
                  Dopo aver confermato, ti verrà chiesto di effettuare il login per completare la prenotazione.
                </p>
              </div>
            )}
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default Prenotazioni;
